// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type
// checks for this file, matching MonthlyBreakdownChart.tsx and LenderBarChart.tsx.
import React, { useMemo } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { Star, Target, Handshake } from 'lucide-react';
import type { LodgementRecord } from '../../hooks/useLodgements';
import {
  TARGET_TYPE_LABELS,
  type LodgementTargetType,
  type TargetProgress,
} from '../../hooks/useLodgementTargets';
import {
  money, dollarsCompact, dollarsFull, attainmentChip, attainmentBar,
  CHART_BLUE, CHART_EMERALD, CHART_AMBER, CHART_VIOLET, CHART_GRID, CHART_AXIS,
} from './LodgementDisplay';

/**
 * The single-person chart strip.
 *
 * WHAT IT REPLACES. The source workbook drew three charts per person per month
 * and the page carried none of them: deals lodged by week, value lodged by
 * week, and a settlement gauge comparing target, lodged and settled. Those are
 * the pictures the tenant reads, so the page draws the same three.
 *
 * BUILT AS A SIBLING, NOT A NEW STYLE. Card framing, gradient wash, heading,
 * grid stroke, axis colour and tooltip chrome all come from the app's existing
 * Recharts components. Anyone who reads the dashboard should recognise these.
 *
 * MONTH VERSUS QUARTER. Weekly buckets only mean something inside one month, so
 * a quarter renders the settlement comparison and the chips alone rather than
 * four bars each covering three different first-weeks.
 *
 * TARGETS ARE READ, NEVER ASSUMED. Every reference line is drawn only where a
 * target record of that type actually exists for this owner. The workbook's
 * canonical pair is 12 lodged DEALS and $5,000,000 SETTLED, so the amount
 * comparison hangs off `settlement_amount`; the lodged-value chart gets a line
 * only if someone has genuinely set a `lodgement_amount` target. Nothing here
 * hardcodes a type or a number.
 */

const WEEK_LABELS = ['1st', '2nd', '3rd', '4th'];
const WEEKS_IN_MONTH = WEEK_LABELS.length;

/** Same rule as the summary panel's weekly columns. Days 1-7, 8-14, 15-21, 22 to end. */
const WEEK_RULE = 'Weeks are days 1 to 7, 8 to 14, 15 to 21, and 22 to the end of the month.';
function weekIndexOf(isoDate: string): number | null {
  const day = Number(isoDate.slice(8, 10));
  if (!Number.isFinite(day) || day < 1) return null;
  return Math.min(WEEKS_IN_MONTH - 1, Math.floor((day - 1) / 7));
}

interface Props {
  /** The complete period, already fetched. */
  rows: LodgementRecord[];
  ownerId: string;
  ownerLabel: string;
  periodLabel: string;
  /** Weekly charts render for a month only. */
  periodKind: 'month' | 'quarter';
  from: string;
  to: string;
  progress: TargetProgress[];
  canManageTargets: boolean;
  onAddTarget: (type: LodgementTargetType) => void;
}

function ChartCard({
  title, subtitle, color, children,
}: {
  title: string; subtitle?: string; color: string; children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="relative overflow-hidden rounded-lg bg-gray-800 p-6 shadow-lg">
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: `radial-gradient(circle at 50% 0%, ${color}40 0%, transparent 70%)` }}
      />
      <div className="relative z-10 mb-4">
        <h3 className="text-lg font-bold text-white">{title}</h3>
        {subtitle && <p className="mt-0.5 text-[11px] text-gray-400">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function TooltipBox({ label, lines }: { label: string; lines: Array<[string, string, string]> }) {
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900 p-4 text-white shadow-xl">
      <p className="mb-2 font-bold">{label}</p>
      {lines.map(([name, value, colour]) => (
        <p key={name} style={{ color: colour }}>
          {name}: {value}
        </p>
      ))}
    </div>
  );
}

export function LodgementCharts({
  rows, ownerId, ownerLabel, periodLabel, periodKind, from, to,
  progress, canManageTargets, onAddTarget,
}: Props): React.ReactElement {
  const mine = useMemo(() => rows.filter((r) => r.ownerId === ownerId), [rows, ownerId]);

  /** Applicable targets only: one whose window excludes this period is not one. */
  const targetFor = (type: LodgementTargetType): TargetProgress | undefined =>
    progress.find((p) => p.ownerId === ownerId && p.type === type && !p.notApplicable);

  const countTarget = targetFor('lodgement_count');
  const lodgedAmountTarget = targetFor('lodgement_amount');
  const settlementTarget = targetFor('settlement_amount');

  /** Weekly buckets. Cents summed as cents, converted to dollars once, at the end. */
  const weekly = useMemo(() => {
    const buckets = WEEK_LABELS.map((week) => ({ week, count: 0, cents: 0 }));
    for (const r of mine) {
      const w = weekIndexOf(r.dateLodged);
      if (w === null) continue;
      buckets[w].count += 1;
      buckets[w].cents += r.lodgeAmountCents || 0;
    }
    return buckets.map((b) => ({ ...b, dollars: b.cents / 100 }));
  }, [mine]);

  const lodgedCents = useMemo(
    () => mine.reduce((a, r) => a + (r.lodgeAmountCents || 0), 0),
    [mine]
  );

  /**
   * Settled value for the comparison chart.
   *
   * With a target this is the server's own actual, which counts by SETTLEMENT
   * date and therefore includes deals lodged before this period. Without one
   * there is only what the page holds, which is rows lodged in the period that
   * also settled in it; the card says so rather than printing a number that
   * looks authoritative.
   */
  const settledCentsLocal = useMemo(
    () => mine
      .filter((r) => r.settledDate && r.settledDate >= from && r.settledDate <= to)
      .reduce((a, r) => a + (r.settledAmountCents || 0), 0),
    [mine, from, to]
  );
  const settledCents = settlementTarget ? settlementTarget.actual : settledCentsLocal;

  const settledDeals = useMemo(
    () => mine.filter((r) => r.settledDate && r.settledDate >= from && r.settledDate <= to).length,
    [mine, from, to]
  );
  const reviewsAsked = useMemo(() => mine.filter((r) => r.googleReviewAsked).length, [mine]);
  const reviewsReceived = useMemo(() => mine.filter((r) => r.googleReviewReceived).length, [mine]);

  /**
   * A MONTHLY target prorated across four weekly bars.
   *
   * Drawing the whole month's target on a weekly chart would put the line far
   * above every bar and read as a catastrophic miss when the month is on track.
   * The pace line is the comparison that means something week to week, and it
   * is labelled with the number it came from so nobody reads it as the target
   * itself. Quarterly targets are not prorated onto weeks at all: this chart
   * only renders for a month.
   */
  const countPace =
    countTarget && countTarget.target > 0 ? countTarget.target / WEEKS_IN_MONTH : null;
  // Cents to dollars first, because the bars this line sits on are in dollars.
  const amountPace =
    lodgedAmountTarget && lodgedAmountTarget.target > 0
      ? (lodgedAmountTarget.target / 100) / WEEKS_IN_MONTH
      : null;

  const comparison = useMemo(() => {
    const bars = [
      { name: 'Lodged', dollars: lodgedCents / 100, fill: CHART_BLUE },
      { name: 'Settled', dollars: settledCents / 100, fill: CHART_EMERALD },
    ];
    if (settlementTarget) {
      bars.unshift({ name: 'Target', dollars: settlementTarget.target / 100, fill: CHART_AMBER });
    }
    return bars;
  }, [lodgedCents, settledCents, settlementTarget]);

  const showWeekly = periodKind === 'month';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">{ownerLabel}, {periodLabel}</h2>
          <p className="mt-0.5 text-[11px] text-gray-400">
            {showWeekly ? WEEK_RULE : 'Weekly charts appear when a single month is selected.'}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800/60 px-3 py-2">
            <Handshake className="h-4 w-4 text-emerald-300" />
            <div>
              <div className="text-lg font-bold leading-none text-white">{settledDeals}</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Settlement deals</div>
            </div>
          </div>
          <div
            className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800/60 px-3 py-2"
            title={`${reviewsReceived} of these were received`}
          >
            <Star className="h-4 w-4 text-amber-300" />
            <div>
              <div className="text-lg font-bold leading-none text-white">{reviewsAsked}</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Google reviews asked</div>
            </div>
          </div>
        </div>
      </div>

      <div className={`grid gap-4 ${showWeekly ? 'xl:grid-cols-3' : 'xl:grid-cols-2'}`}>
        {/*
          VS-TARGET BARS, FOR QUARTER GRAIN ONLY.

          A quarter gets no weekly charts, and the settlement chart beside this
          covers only settlement_amount. Without this card a person holding a
          deal-count or lodged-value target would see nothing at all about it
          the moment the period widened from a month to a quarter, which is a
          worse answer than the plain bars this feature started with. So the
          bars survive exactly where a chart cannot replace them.
        */}
        {!showWeekly && (
          <ChartCard
            title="Lodgement targets"
            subtitle="Deals and value lodged in the period, against whatever targets exist"
            color={CHART_BLUE}
          >
            <div className="relative z-10 space-y-4">
              {([
                ['lodgement_count', countTarget, mine.length, false],
                ['lodgement_amount', lodgedAmountTarget, lodgedCents, true],
              ] as Array<[LodgementTargetType, TargetProgress | undefined, number, boolean]>).map(
                ([type, target, localActual, isMoney]) => {
                  const actual = target ? target.actual : localActual;
                  const fmtOne = (n: number) => (isMoney ? money(n) : String(n));
                  return (
                    <div key={type}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm text-gray-300">{TARGET_TYPE_LABELS[type]}</span>
                        {target ? (
                          <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${attainmentChip(target.percent)}`}>
                            {target.percent}%
                          </span>
                        ) : (
                          <span className="text-[11px] text-gray-500">no target set</span>
                        )}
                      </div>
                      <div
                        className={`mt-1.5 h-5 overflow-hidden rounded bg-gray-700/40 ${
                          target ? '' : 'border border-dashed border-gray-600'
                        }`}
                      >
                        {target && (
                          <div
                            className={`h-full ${attainmentBar(target.percent)}`}
                            style={{ width: `${Math.min(100, Math.max(0, target.percent))}%` }}
                            title={`${fmtOne(actual)} of ${fmtOne(target.target)}`}
                          />
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <span className="text-[11px] tabular-nums text-gray-400">
                          {target
                            ? `${fmtOne(actual)} of ${fmtOne(target.target)}`
                            : `${fmtOne(actual)} so far`}
                        </span>
                        {!target && canManageTargets && (
                          <button
                            type="button"
                            onClick={() => onAddTarget(type)}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-700 px-2 py-0.5 text-[11px] text-blue-300 hover:bg-gray-800"
                          >
                            <Target className="h-3 w-3" />
                            Set a target
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </ChartCard>
        )}

        {showWeekly && (
          <ChartCard
            title="Lodgements by week"
            color={CHART_BLUE}
            subtitle={
              countTarget
                ? `Pace line is ${(countTarget.target / WEEKS_IN_MONTH).toFixed(1)} a week, from a target of ${countTarget.target} for the month`
                : 'No deal-count target set for this person'
            }
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={weekly} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.3} />
                <XAxis dataKey="week" stroke={CHART_AXIS} fontSize={12} />
                <YAxis
                  stroke={CHART_AXIS}
                  fontSize={12}
                  allowDecimals={false}
                  tickFormatter={(v) => (Number.isFinite(v) ? String(v) : '0')}
                />
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <TooltipBox
                        label={`${label} week`}
                        lines={[
                          ['Lodgements', String(payload[0].payload.count), CHART_BLUE],
                          ['Value lodged', dollarsFull(payload[0].payload.dollars), CHART_AXIS],
                        ]}
                      />
                    ) : null
                  }
                />
                {countPace !== null && (
                  <ReferenceLine
                    y={countPace}
                    stroke={CHART_AMBER}
                    strokeDasharray="4 4"
                    label={{ value: 'weekly pace', position: 'insideTopRight', fill: CHART_AMBER, fontSize: 11 }}
                  />
                )}
                <Bar dataKey="count" fill={CHART_BLUE} radius={[4, 4, 0, 0]} animationDuration={800} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {showWeekly && (
          <ChartCard
            title="Lodged amount by week"
            color={CHART_VIOLET}
            subtitle={
              lodgedAmountTarget
                ? `Pace line is ${dollarsCompact((lodgedAmountTarget.target / 100) / WEEKS_IN_MONTH)} a week, from ${money(lodgedAmountTarget.target)} for the month`
                : 'No lodged-value target set; the tenant measures value against settlements'
            }
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={weekly} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.3} />
                <XAxis dataKey="week" stroke={CHART_AXIS} fontSize={12} />
                <YAxis stroke={CHART_AXIS} fontSize={12} tickFormatter={dollarsCompact} />
                <Tooltip
                  cursor={false}
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <TooltipBox
                        label={`${label} week`}
                        lines={[
                          ['Value lodged', dollarsFull(payload[0].payload.dollars), CHART_VIOLET],
                          ['Lodgements', String(payload[0].payload.count), CHART_AXIS],
                        ]}
                      />
                    ) : null
                  }
                />
                {amountPace !== null && (
                  <ReferenceLine
                    y={amountPace}
                    stroke={CHART_AMBER}
                    strokeDasharray="4 4"
                    label={{ value: 'weekly pace', position: 'insideTopRight', fill: CHART_AMBER, fontSize: 11 }}
                  />
                )}
                <Bar dataKey="dollars" fill={CHART_VIOLET} radius={[4, 4, 0, 0]} animationDuration={800} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        <ChartCard
          title="Settlement target"
          color={CHART_EMERALD}
          subtitle={
            settlementTarget
              ? `${settlementTarget.percent}% of ${money(settlementTarget.target)} settled`
              : 'No settlement target set for this person'
          }
        >
          {settlementTarget ? (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={comparison}
                  layout="vertical"
                  margin={{ top: 5, right: 90, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.3} />
                  <XAxis type="number" stroke={CHART_AXIS} fontSize={12} tickFormatter={dollarsCompact} />
                  <YAxis type="category" dataKey="name" stroke={CHART_AXIS} fontSize={12} width={70} />
                  <Tooltip
                    cursor={false}
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <TooltipBox
                          label={String(label)}
                          lines={[['Amount', dollarsFull(payload[0].payload.dollars), payload[0].payload.fill]]}
                        />
                      ) : null
                    }
                  />
                  <Bar
                    dataKey="dollars"
                    radius={[0, 4, 4, 0]}
                    animationDuration={800}
                    label={({ x, y, width, value }) =>
                      Number.isFinite(value) && value > 0 ? (
                        <text x={x + width + 6} y={y + 14} fill="#ffffff" fontSize={11} fontWeight="500">
                          {dollarsCompact(value)}
                        </text>
                      ) : null
                    }
                  >
                    {comparison.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="relative z-10 mt-2 text-center">
                <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${attainmentChip(settlementTarget.percent)}`}>
                  {settlementTarget.percent}% of settlement target
                </span>
              </div>
            </>
          ) : (
            /*
              No target, so no comparison: a chart with an absent denominator
              would draw two bars against nothing and imply a judgement that was
              never agreed. One click goes to the screen that sets it.
            */
            <div className="relative z-10 rounded-lg border border-dashed border-gray-600 bg-gray-900/40 p-8 text-center">
              <p className="text-sm text-gray-300">No settlement target set</p>
              <p className="mt-1 text-[11px] text-gray-500">
                {money(lodgedCents)} lodged and {money(settledCentsLocal)} settled in this period.
                Settled counts deals lodged in this period that also settled in it.
              </p>
              {canManageTargets && (
                <button
                  type="button"
                  onClick={() => onAddTarget('settlement_amount')}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
                >
                  <Target className="h-4 w-4" />
                  Set a settlement target
                </button>
              )}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
